import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_ROUTES } from "../constants";
import type { IWidgetOperation } from "../interfaces/cash-deposit";
import { useStore } from "../store/globalState";
import { useCashDepositStore } from "../store/useCashDepositStore";
import { useSessionStore } from "../store/useSessionStore";
import {
  isCashDepositFundingFailed,
  isCashDepositFundingSucceeded,
  MINTABLE_FUNDING_STATUSES,
} from "../utils/already-funded";
import { normalizeRoutePath } from "../utils/route-path";
import { useCashDepositContext } from "./useCashDepositContext";
import { useOperationSSE } from "./useOperationSSE";
import { useRouteWithToken } from "./useRouteWithToken";

const FUNDING_FUNNEL_PATHS = new Set<string>([
  APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE,
  APP_ROUTES.STORES_PAGE,
  APP_ROUTES.CASH_STORES_PAGE,
  APP_ROUTES.HOW_TO_PAY_IN_CASH_PAGE,
  APP_ROUTES.STORES_MAP_PAGE,
]);

const MINTABLE = new Set<string>(MINTABLE_FUNDING_STATUSES);

/**
 * Live funding watch for the cash-deposit funnel. Independent of the barcode
 * drawer: closing the popup or opening how-to / map must still see
 * `fundingStatus: funded`, `status: processing|completed`, and cashier
 * amount-mismatch attempts. Happy-path amounts emit processing (not PIN).
 */
export function useCashDepositFundingWatch(): void {
  const { t } = useTranslation();
  const location = useLocation();
  const { redirectTo } = useRouteWithToken();
  const { context } = useCashDepositContext();
  const setErrorState = useStore((s) => s.setErrorState);
  const linkId = useSessionStore((s) => s.session.data?.linkId);
  const seenFundingAttemptKeys = useCashDepositStore(
    (s) => s.seenFundingAttemptKeys,
  );
  const markFundingAttemptSeen = useCashDepositStore(
    (s) => s.markFundingAttemptSeen,
  );
  const setPendingMismatch = useCashDepositStore((s) => s.setPendingMismatch);

  const operationId = context?.operationId;
  const fundingStatus = context?.fundingStatus;
  const enabled =
    Boolean(operationId) &&
    Boolean(linkId) &&
    (!fundingStatus || MINTABLE.has(fundingStatus)) &&
    FUNDING_FUNNEL_PATHS.has(normalizeRoutePath(location.pathname));

  const consumeFundingAttempts = (operation: IWidgetOperation) => {
    if (isCashDepositFundingSucceeded(operation)) {
      redirectTo(APP_ROUTES.PAYMENT_VERIFICATION_PAGE);
      return;
    }

    if (isCashDepositFundingFailed(operation)) {
      const error = {
        title: t("error.title"),
        subTitle: t("error.unexpectedError"),
        message: t("error.tryAgainLater"),
        btnText: t("common.needHelp"),
        isOut: false,
        pageUrl: APP_ROUTES.SUPPORT_PAGE,
      };
      setErrorState(error);
      redirectTo(APP_ROUTES.ERROR_PAGE, { replace: true, state: error });
      return;
    }

    const attempts = operation.fundingAttempts ?? [];
    const unseen = [...attempts].reverse().find((attempt) => {
      const key = attempt.attemptRef ?? attempt.occurredAt;
      return !seenFundingAttemptKeys.includes(key);
    });
    if (!unseen) return;
    const key = unseen.attemptRef ?? unseen.occurredAt;
    markFundingAttemptSeen(key);
    setPendingMismatch(unseen);
  };

  useOperationSSE({
    enabled,
    linkId,
    operationId,
    onOperation: consumeFundingAttempts,
  });
}
